"use client";

import { c as _c } from "react/compiler-runtime";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getWalletAddress, removeAuthJwt } from "@/services/auth/auth.utils";
import { WalletInitializationError } from "@/src/errors/wallet";
import { SecurityEventType } from "@/src/types/security";
import { createConnectionEventContext, createValidationEventContext, logError, logSecurityEvent } from "@/src/utils/security-logger";
import { useAppKit, useAppKitAccount, useAppKitState, useDisconnect } from "@reown/appkit/react";
import { getAddress, isAddress } from "viem";
import { WalletErrorBoundary } from "./error-boundary";

// Custom error types for better error handling
class WalletConnectionError extends Error {
  constructor(message: string, public readonly cause?: unknown, public readonly code?: string) {
    super(message);
    this.name = "WalletConnectionError";
  }
}
class WalletDisconnectionError extends Error {
  constructor(message: string, public readonly cause?: unknown, public readonly code?: string) {
    super(message);
    this.name = "WalletDisconnectionError";
  }
}
class AuthenticationError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = "AuthenticationError";
  }
}
interface SeizeConnectContextType {
  /** Current connected wallet address, undefined if not connected */
  address: string | undefined;

  /** Name of the connected wallet (e.g. "MetaMask", "Trust Wallet") */
  walletName: string | undefined;

  /** Icon URL of the connected wallet */
  walletIcon: string | undefined;

  /** Whether the connected wallet is a Safe (Gnosis Safe) wallet */
  isSafeWallet: boolean;

  /** Opens the wallet connection modal */
  seizeConnect: () => void;

  /**
   * Disconnects the current wallet connection
   * @throws {WalletDisconnectionError} When disconnection fails
   */
  seizeDisconnect: () => Promise<void>;

  /**
   * Disconnects wallet and clears authentication state
   * @param reconnect - Whether to automatically reopen connection modal after logout
   * @throws {WalletDisconnectionError} When disconnection fails
   * @throws {AuthenticationError} When auth cleanup fails
   */
  seizeDisconnectAndLogout: (reconnect?: boolean) => Promise<void>;

  /**
   * Manually set the connected address (for internal use)
   * @param address - The wallet address to set as connected
   */
  seizeAcceptConnection: (address: string) => void;

  /** Whether the connection modal is currently open */
  seizeConnectOpen: boolean;

  /** Whether a wallet is currently connected to the app */
  isConnected: boolean;

  /** Whether the user is authenticated with a wallet address */
  isAuthenticated: boolean;

  /** Current connection state for better timing control */
  connectionState: "initializing" | "disconnected" | "connecting" | "connected" | "error";

  /** Unified wallet state machine for advanced consumers */
  walletState: WalletState;

  /** Whether there was an initialization error */
  hasInitializationError: boolean;

  /** The initialization error if one occurred */
  initializationError: Error | undefined;
}
const SeizeConnectContext = createContext<SeizeConnectContextType | undefined>(undefined);

// Error handling utilities
const createWalletError = (ErrorClass: typeof WalletConnectionError | typeof WalletDisconnectionError, operation: string, originalError: unknown): WalletConnectionError | WalletDisconnectionError => {
  const message = originalError instanceof Error ? originalError.message : `Unknown error during ${operation}`;
  return new ErrorClass(`Failed to ${operation}: ${message}`, originalError, originalError instanceof Error ? originalError.name : undefined);
};

// Address validation utilities
interface AddressValidationResult {
  isValid: boolean;
  normalizedAddress?: string;
  errorContext?: {
    length: number;
    format: "hex_prefixed" | "other";
    debugAddress: string;
  };
}
const isCapacitorPlatform = (): boolean => {
  return !!globalThis.window?.Capacitor?.isNativePlatform?.();
};
const validateStoredAddress = (storedAddress: string): AddressValidationResult => {
  // Capacitor-specific validation (more lenient)
  if (isCapacitorPlatform()) {
    if (storedAddress.startsWith("0x") && storedAddress.length === 42) {
      return {
        isValid: true,
        normalizedAddress: storedAddress.toLowerCase()
      };
    }
  }

  // Standard validation using viem
  if (isAddress(storedAddress)) {
    return {
      isValid: true,
      normalizedAddress: getAddress(storedAddress) // checksummed format
    };
  }

  // Invalid address - prepare error context
  const addressLength = storedAddress.length;
  const addressFormat = storedAddress.startsWith("0x") ? "hex_prefixed" : "other";
  const debugAddress = storedAddress.length >= 10 ? storedAddress.slice(0, 10) + "..." : storedAddress;
  return {
    isValid: false,
    errorContext: {
      length: addressLength,
      format: addressFormat,
      debugAddress
    }
  };
};

// Unified Wallet State Machine - eliminates multiple state variables and inconsistencies
type WalletState = {
  status: "initializing";
} | {
  status: "error";
  error: Error;
} | {
  status: "disconnected";
} | {
  status: "connecting";
} | {
  status: "connected";
  address: string;
};

// Initialization error handling utility
const handleInitializationError = (error: unknown, errorContext?: AddressValidationResult["errorContext"]): WalletInitializationError => {
  if (errorContext) {
    // Invalid address error
    logSecurityEvent(SecurityEventType.INVALID_ADDRESS_DETECTED, createValidationEventContext("wallet_initialization", false, errorContext.length, errorContext.format));

    // Clear invalid stored address
    try {
      removeAuthJwt();
    } catch (cleanupError) {
      logError("auth_cleanup_during_init", new Error(`Failed to clear invalid auth state: ${cleanupError}`));
    }
    const initError = new WalletInitializationError("Invalid wallet address found in storage during initialization. This indicates potential data corruption or security breach.", undefined, errorContext.debugAddress);
    logError("wallet_initialization", initError);
    return initError;
  }

  // Unexpected error
  const initError = new WalletInitializationError("Unexpected error during wallet initialization", error);
  logError("wallet_initialization", initError);
  return initError;
};

// Consolidated wallet state management hook with unified state machine
const useConsolidatedWalletState = () => {
  const [walletState, setWalletState] = useState<WalletState>({
    status: "initializing"
  });
  useEffect(() => {
    const initializeWallet = async () => {
      try {
        // Step 1: Retrieve stored address
        const storedAddress: string | null = getWalletAddress();

        // Step 2: Handle no stored address (legitimate case)
        if (!storedAddress) {
          setWalletState({
            status: "disconnected"
          });
          return;
        }

        // Step 3: Validate stored address
        const validationResult = validateStoredAddress(storedAddress);
        if (validationResult.isValid && validationResult.normalizedAddress) {
          // Step 4: Success - set connected state with valid address
          setWalletState({
            status: "connected",
            address: validationResult.normalizedAddress
          });
        } else {
          // Step 4: Error - handle invalid address
          const error_0 = handleInitializationError(undefined, validationResult.errorContext);
          setWalletState({
            status: "error",
            error: error_0
          });
        }
      } catch (error) {
        // Step 4: Error - handle unexpected errors
        const initError = handleInitializationError(error);
        setWalletState({
          status: "error",
          error: initError
        });
      }
    };
    initializeWallet();
  }, []);

  // State transition methods
  const setConnecting = useCallback(() => {
    setWalletState({
      status: "connecting"
    });
  }, []);
  const setConnected = useCallback((address: string) => {
    setWalletState({
      status: "connected",
      address
    });
  }, []);
  const setDisconnected = useCallback(() => {
    setWalletState({
      status: "disconnected"
    });
  }, []);
  const setError = useCallback((error_1: Error) => {
    setWalletState({
      status: "error",
      error: error_1
    });
  }, []);

  // Computed properties for backward compatibility
  const connectedAddress = walletState.status === "connected" ? walletState.address : undefined;
  const hasInitializationError = walletState.status === "error";
  const initializationError = walletState.status === "error" ? walletState.error : undefined;
  const isInitialized = walletState.status !== "initializing";
  return {
    walletState,
    connectedAddress,
    setConnecting,
    setConnected,
    setDisconnected,
    setError,
    // Backward compatibility properties
    hasInitializationError,
    initializationError,
    isInitialized
  };
};
export const SeizeConnectProvider: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(44);
  const {
    children
  } = t0;
  const account = useAppKitAccount();
  const {
    disconnect
  } = useDisconnect();
  const {
    open
  } = useAppKit();
  const state = useAppKitState();
  const {
    walletState,
    connectedAddress,
    setConnecting,
    setConnected,
    setDisconnected,
    hasInitializationError,
    initializationError,
    isInitialized
  } = useConsolidatedWalletState();
  const debounceTimeoutRef = useRef(null);
  let t1;
  if ($[0] !== account.address || $[1] !== account.isConnected || $[2] !== account.status || $[3] !== isInitialized || $[4] !== setConnected || $[5] !== setConnecting || $[6] !== setDisconnected || $[7] !== walletState.address || $[8] !== walletState.status) {
    t1 = () => {
      if (!isInitialized) {
        return;
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        if (account.address && account.isConnected) {
          if (isAddress(account.address)) {
            const checksummedAddress = getAddress(account.address);
            const isAlreadyConnected = walletState.status === "connected" && walletState.address === checksummedAddress;
            if (!isAlreadyConnected) {
              setConnected(checksummedAddress);
            }
          } else {
            const addressStr = account.address as string | undefined;
            logSecurityEvent(SecurityEventType.INVALID_ADDRESS_DETECTED, createValidationEventContext("wallet_provider", false, addressStr?.length || 0, addressStr?.startsWith("0x") ? "hex_prefixed" : "other"));
            setDisconnected();
          }
        } else {
          if (account.isConnected === false) {
            const storedAddress = getWalletAddress();
            if (storedAddress && isAddress(storedAddress)) {
              const checksummedAddress_0 = getAddress(storedAddress);
              const isAlreadyConnected_0 = walletState.status === "connected" && walletState.address === checksummedAddress_0;
              if (!isAlreadyConnected_0) {
                setConnected(checksummedAddress_0);
              }
            } else {
              if (walletState.status !== "disconnected") {
                setDisconnected();
              }
            }
          } else {
            if (account.status === "connecting") {
              if (walletState.status !== "connecting") {
                setConnecting();
              }
            } else {
              if (walletState.status !== "disconnected") {
                setDisconnected();
              }
            }
          }
        }
      }, 50);
      return () => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
      };
    };
    $[0] = account.address;
    $[1] = account.isConnected;
    $[2] = account.status;
    $[3] = isInitialized;
    $[4] = setConnected;
    $[5] = setConnecting;
    $[6] = setDisconnected;
    $[7] = walletState.address;
    $[8] = walletState.status;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== account.address || $[11] !== account.isConnected || $[12] !== account.status || $[13] !== isInitialized || $[14] !== setConnected || $[15] !== setConnecting || $[16] !== setDisconnected || $[17] !== walletState) {
    t2 = [account.address, account.isConnected, account.status, isInitialized, walletState, setConnected, setDisconnected, setConnecting];
    $[10] = account.address;
    $[11] = account.isConnected;
    $[12] = account.status;
    $[13] = isInitialized;
    $[14] = setConnected;
    $[15] = setConnecting;
    $[16] = setDisconnected;
    $[17] = walletState;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  useEffect(t1, t2);
  let t3;
  if ($[19] !== open) {
    t3 = () => {
      ;
      try {
        logSecurityEvent(SecurityEventType.WALLET_CONNECTION_ATTEMPT, createConnectionEventContext("seizeConnect"));
        open({
          view: "Connect"
        });
        logSecurityEvent(SecurityEventType.WALLET_MODAL_OPENED, createConnectionEventContext("seizeConnect"));
      } catch (t4) {
        const error = t4;
        const connectionError = new WalletConnectionError("Failed to open wallet connection modal", error);
        logError("seizeConnect", connectionError);
        throw connectionError;
      }
    };
    $[19] = open;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  const seizeConnect = t3;
  let t4;
  if ($[21] !== disconnect) {
    t4 = async () => {
      ;
      try {
        await disconnect();
      } catch (t5) {
        const error_0 = t5;
        const walletError = createWalletError(WalletDisconnectionError, "disconnect wallet", error_0);
        logError("seizeDisconnect", walletError);
        throw walletError;
      }
    };
    $[21] = disconnect;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  const seizeDisconnect = t4;
  let t5;
  if ($[23] !== disconnect || $[24] !== open || $[25] !== setDisconnected) {
    t5 = async reconnect => {
      ;
      try {
        await disconnect();
      } catch (t6) {
        const error_1 = t6;
        const walletError_0 = createWalletError(WalletDisconnectionError, "disconnect wallet during logout", error_1);
        logError("seizeDisconnectAndLogout", walletError_0);
        throw new AuthenticationError("Cannot complete logout: wallet disconnection failed. User may still have active wallet connection.", walletError_0);
      }
      ;
      try {
        removeAuthJwt();
        setDisconnected();
        if (reconnect) {
          setTimeout(() => {
            ;
            try {
              logSecurityEvent(SecurityEventType.WALLET_CONNECTION_ATTEMPT, createConnectionEventContext("seizeDisconnectAndLogout_reconnect"));
              open({
                view: "Connect"
              });
            } catch (t8) {
              const openError = t8;
              logError("seizeDisconnectAndLogout_reconnect", openError instanceof Error ? openError : new Error(`Failed to reopen wallet connection: ${openError}`));
            }
          }, 100);
        }
      } catch (t7) {
        const error_2 = t7;
        const authError = new AuthenticationError("Failed to clear authentication state after successful wallet disconnect", error_2);
        logError("seizeDisconnectAndLogout", authError);
        throw authError;
      }
    };
    $[23] = disconnect;
    $[24] = open;
    $[25] = setDisconnected;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  const seizeDisconnectAndLogout = t5;
  let t6;
  if ($[27] !== setConnected) {
    t6 = address => {
      const addressLength = address.length;
      const addressFormat = address.startsWith("0x") ? "hex_prefixed" : "other";
      if (!isAddress(address)) {
        logSecurityEvent(SecurityEventType.INVALID_ADDRESS_DETECTED, createValidationEventContext("seizeAcceptConnection", false, addressLength, addressFormat));
        const error_3 = new AuthenticationError("Invalid Ethereum address format. Address must be a valid EIP-55 checksummed format.");
        logError("seizeAcceptConnection", error_3);
        throw error_3;
      }
      logSecurityEvent(SecurityEventType.ADDRESS_VALIDATION_SUCCESS, createValidationEventContext("seizeAcceptConnection", true));
      const checksummedAddress_1 = getAddress(address);
      setConnected(checksummedAddress_1);
    };
    $[27] = setConnected;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  const seizeAcceptConnection = t6;
  const t7 = !!connectedAddress;
  let t8;
  if ($[29] !== account.isConnected || $[30] !== connectedAddress || $[31] !== hasInitializationError || $[32] !== initializationError || $[33] !== seizeAcceptConnection || $[34] !== seizeConnect || $[35] !== seizeDisconnect || $[36] !== seizeDisconnectAndLogout || $[37] !== state.open || $[38] !== t7 || $[39] !== walletState) {
    t8 = {
      address: connectedAddress,
      walletName: undefined,
      walletIcon: undefined,
      isSafeWallet: false,
      seizeConnect,
      seizeDisconnect,
      seizeDisconnectAndLogout,
      seizeAcceptConnection,
      seizeConnectOpen: state.open,
      isConnected: account.isConnected,
      isAuthenticated: t7,
      connectionState: walletState.status,
      walletState,
      hasInitializationError,
      initializationError
    };
    $[29] = account.isConnected;
    $[30] = connectedAddress;
    $[31] = hasInitializationError;
    $[32] = initializationError;
    $[33] = seizeAcceptConnection;
    $[34] = seizeConnect;
    $[35] = seizeDisconnect;
    $[36] = seizeDisconnectAndLogout;
    $[37] = state.open;
    $[38] = t7;
    $[39] = walletState;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  const contextValue = t8;
  let t9;
  if ($[41] !== children || $[42] !== contextValue) {
    t9 = <WalletErrorBoundary><SeizeConnectContext.Provider value={contextValue}>{children}</SeizeConnectContext.Provider></WalletErrorBoundary>;
    $[41] = children;
    $[42] = contextValue;
    $[43] = t9;
  } else {
    t9 = $[43];
  }
  return t9;
};
export const useSeizeConnectContext = () => {
  const context = useContext(SeizeConnectContext);
  if (!context) {
    throw new Error("useSeizeConnectContext must be used within a SeizeConnectProvider");
  }
  return context;
};